import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  FileAttachmentField,
  type FileAttachmentRow,
} from "@/components/shared/file-attachment-field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { JOURNEY_ATTACHMENT_MIME_ALLOWLIST } from "@colab/shared";
import { useJourneyTaskAttachments } from "./use-journey-task-attachments";
import { useUploadTaskAttachment } from "./use-upload-task-attachment";
import { useRemoveTaskAttachment } from "./use-remove-task-attachment";
import { useRecordProofPayload } from "./use-record-proof-payload";
import { fetchJourneyTaskAttachmentUrl } from "./use-task-attachment-url";
import type { PortalJourneyProgressWithTask } from "./types";

// Discriminated render for the three proof-requiring action kinds. Each
// variant collects its specific payload + calls useRecordProofPayload with
// completeOnRecord=true so the click both records the proof AND flips the
// task to completed.

type Props = {
  progress: PortalJourneyProgressWithTask;
  assignmentId: string;
};

export function TaskProofInput({ progress, assignmentId }: Props) {
  const action = progress.task.action;
  if (action.kind === "note") {
    return (
      <NoteProofInput
        progressId={progress.id}
        assignmentId={assignmentId}
        prompt={action.prompt ?? null}
        existing={
          progress.proof_payload?.kind === "note"
            ? progress.proof_payload.note
            : null
        }
        isComplete={progress.status === "completed"}
      />
    );
  }
  if (action.kind === "link") {
    return (
      <LinkProofInput
        progressId={progress.id}
        assignmentId={assignmentId}
        prompt={action.prompt ?? null}
        existing={
          progress.proof_payload?.kind === "link"
            ? progress.proof_payload.url
            : null
        }
        isComplete={progress.status === "completed"}
      />
    );
  }
  if (action.kind === "attachment") {
    return (
      <AttachmentProofInput
        progressId={progress.id}
        assignmentId={assignmentId}
        prompt={action.prompt ?? null}
        multiple={action.multiple ?? false}
        isComplete={progress.status === "completed"}
      />
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// note
// ---------------------------------------------------------------------------
function NoteProofInput({
  progressId,
  assignmentId,
  prompt,
  existing,
  isComplete,
}: {
  progressId: string;
  assignmentId: string;
  prompt: string | null;
  existing: string | null;
  isComplete: boolean;
}) {
  const [note, setNote] = useState(existing ?? "");
  const record = useRecordProofPayload();
  const disabled = isComplete || record.isPending;

  function handleSubmit() {
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error("Add a note before completing.");
      return;
    }
    record.mutate({
      progressId,
      assignmentId,
      payload: { kind: "note", note: trimmed },
    });
  }

  return (
    <div className="space-y-2">
      {prompt ? (
        <p className="text-xs text-muted-foreground">{prompt}</p>
      ) : null}
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={disabled}
        rows={3}
        maxLength={4000}
        placeholder="Add a note recording what you did…"
      />
      {!isComplete ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={disabled || note.trim().length === 0}
          >
            {record.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Record &amp; complete
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// link
// ---------------------------------------------------------------------------
function LinkProofInput({
  progressId,
  assignmentId,
  prompt,
  existing,
  isComplete,
}: {
  progressId: string;
  assignmentId: string;
  prompt: string | null;
  existing: string | null;
  isComplete: boolean;
}) {
  const [url, setUrl] = useState(existing ?? "");
  const record = useRecordProofPayload();
  const disabled = isComplete || record.isPending;

  function handleSubmit() {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error("Add a URL before completing.");
      return;
    }
    record.mutate({
      progressId,
      assignmentId,
      payload: { kind: "link", url: trimmed },
    });
  }

  return (
    <div className="space-y-2">
      {prompt ? (
        <p className="text-xs text-muted-foreground">{prompt}</p>
      ) : null}
      {isComplete && existing ? (
        <a
          href={existing}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-accent underline-offset-4 hover:underline break-all"
        >
          {existing}
        </a>
      ) : (
        <>
          <Input
            type="url"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={disabled}
            maxLength={2048}
          />
          {!isComplete ? (
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit}
                disabled={disabled || url.trim().length === 0}
              >
                {record.isPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : null}
                Record &amp; complete
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// attachment
// ---------------------------------------------------------------------------
function AttachmentProofInput({
  progressId,
  assignmentId,
  prompt,
  multiple,
  isComplete,
}: {
  progressId: string;
  assignmentId: string;
  prompt: string | null;
  multiple: boolean;
  isComplete: boolean;
}) {
  const attachmentsQuery = useJourneyTaskAttachments(progressId);
  const upload = useUploadTaskAttachment(progressId);
  const remove = useRemoveTaskAttachment();
  const record = useRecordProofPayload();

  const rows: FileAttachmentRow[] = (attachmentsQuery.data ?? []).map((a) => ({
    id: a.id,
    file_name: a.file_name,
    size_bytes: a.size_bytes,
    content_type: a.content_type,
  }));

  function handleComplete() {
    if (rows.length === 0) {
      toast.error("Upload at least one file before completing.");
      return;
    }
    record.mutate({
      progressId,
      assignmentId,
      payload: {
        kind: "attachment",
        attachment_ids: rows.map((r) => r.id),
      },
    });
  }

  return (
    <div className="space-y-3">
      {prompt ? (
        <p className="text-xs text-muted-foreground">{prompt}</p>
      ) : null}
      <FileAttachmentField
        label={null}
        helpText={null}
        attachments={rows}
        multiple={multiple}
        maxFiles={multiple ? 10 : 1}
        acceptedMimeTypes={JOURNEY_ATTACHMENT_MIME_ALLOWLIST}
        disabled={isComplete}
        onUpload={async (file) => {
          const result = await upload.mutateAsync(file);
          return {
            id: result.id,
            file_name: result.file_name,
            size_bytes: result.size_bytes,
            content_type: result.content_type,
          };
        }}
        onRemove={async (id) => {
          await remove.mutateAsync({ id, progressId });
        }}
        getDownloadUrl={fetchJourneyTaskAttachmentUrl}
      />
      {!isComplete ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleComplete}
            disabled={record.isPending || rows.length === 0}
          >
            {record.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Record &amp; complete
          </Button>
        </div>
      ) : null}
    </div>
  );
}
